import React from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { useTypewriter, Cursor } from "react-simple-typewriter";
import { toast } from "react-toastify";
import { Link as ScrollLink } from "react-scroll";
import elevation from "../../assets/elevation.png";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import brousher from "../../assets/brousher.pdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import "./hero.css";
import downLoadBrousher from "../../utils/brousherDownload";
import openWhatsApp from "../../utils/sendWhatsapp";
const Hero = () => {
  const { ref, inView } = useInView({ threshold: 0.25, triggerOnce: true });

  const [typedText] = useTypewriter({
    words: [
      "Luxury Residences.",
      "Smart Living.",
      "Prime Location.",
      "Flexible Payment Plans.",
    ],
    loop: true,
    typeSpeed: 42,
    deleteSpeed: 22,
    delaySpeed: 1600,
  });

  const handleBookVisit = () => {
    // toast.info("Booking form will open here (we'll connect it next).");
    openWhatsApp();
  };

  const handleDownloadBrochure = () => {
    downLoadBrousher();
    toast.success("Download started");
  };

  return (
    <section className="heroV2" ref={ref} id="home">
      {/* Background (no video) */}
      <div className="heroV2__bg">
        <div className="heroV2__gradient" />
        <div className="heroV2__noise" />
        <div className="heroV2__glow heroV2__glow--a" />
        <div className="heroV2__glow heroV2__glow--b" />
      </div>

      <div className="heroV2__container">
        <div className="heroV2__grid">
          {/* Left content */}
          <div className="heroV2__left">
            <motion.h2
              className="heroV2__superTitle"
              initial={{ opacity: 0, y: 14 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              HB Height <span>By Hafiz Brothers</span>
            </motion.h2>

            <motion.div
              className="heroV2__badge"
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7 }}
            >
              <span className="heroV2__brand">HB HEIGHT</span>
              <span className="heroV2__sep" />
              <span className="heroV2__mini">Premium High-Rise Project</span>
            </motion.div>

            <motion.h1
              className="heroV2__title"
              initial={{ opacity: 0, y: 18 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.05 }}
            >
              Live in a space that feels premium — every day.
            </motion.h1>

            <motion.div
              className="heroV2__typed"
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.12 }}
            >
              <span>{typedText}</span>
              <Cursor cursorStyle="|" cursorBlinking />
            </motion.div>

            <motion.p
              className="heroV2__sub"
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.18 }}
            >
              HB Height brings modern design, clean amenities, strong security,
              and a payment plan built for real life — not just brochures.
            </motion.p>

            <motion.div
              className="heroV2__ctaRow"
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.24 }}
            >
              <button
                className="heroV2__btn heroV2__btn--primary"
                onClick={handleBookVisit}
              >
                Book a Site Visit <ArrowOutwardRoundedIcon />
              </button>

              <button
                className="heroV2__btn heroV2__btn--ghost"
                onClick={handleDownloadBrochure}
              >
                View Brochure <VisibilityIcon />
              </button>

              {/* <ScrollLink
                to="heroStats"
                smooth={true}
                duration={650}
                offset={-20}
                className="heroV2__chip"
              >
                Explore Details ↓
              </ScrollLink> */}
            </motion.div>

            <motion.div
              className="heroV2__trust"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.32 }}
            >
              <span>✓ Approved Project</span>
              <span>✓ Limited Units</span>
              <span>✓ Secure Investment</span>
            </motion.div>
          </div>

          {/* Right visual card */}
          <motion.div
            className="heroV2__right"
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <div className="heroV2__poster">
              <img src={elevation} alt="HB Height Visual" loading="lazy" />
              <div className="heroV2__posterShade" />

              <div className="heroV2__posterCard">
                <div className="heroV2__posterTop">
                  <div className="heroV2__posterTitle">HB Height</div>
                  <div className="heroV2__posterTag">High-Rise Living</div>
                </div>

                <div className="heroV2__posterRow">
                  <div className="heroV2__posterKey">Status</div>
                  <div className="heroV2__posterVal">Booking Open</div>
                </div>
                <div className="heroV2__posterRow">
                  <div className="heroV2__posterKey">Possession</div>
                  <div className="heroV2__posterVal">2028</div>
                </div>
                <div className="heroV2__posterRow">
                  <div className="heroV2__posterKey">Focus</div>
                  <div className="heroV2__posterVal">
                    Luxury + Smart Amenities
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats bar */}
        <motion.div
          id="heroStats"
          className="heroV2__stats"
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.28 }}
        >
          <div className="heroV2__stat">
            <div className="heroV2__statNum">
              {inView ? <CountUp end={8} duration={1.6} /> : 0}+
            </div>
            <div className="heroV2__statLabel">Floors</div>
          </div>

          <div className="heroV2__stat">
            <div className="heroV2__statNum">
              {inView ? <CountUp end={65} duration={1.6} /> : 0}+
            </div>
            <div className="heroV2__statLabel">Luxury Units</div>
          </div>

          <div className="heroV2__stat">
            <div className="heroV2__statNum">
              {inView ? <CountUp end={22} duration={1.6} /> : 0}+
            </div>
            <div className="heroV2__statLabel">Amenities</div>
          </div>

          <div className="heroV2__stat">
            <div className="heroV2__statNum">Q4</div>
            <div className="heroV2__statLabel">Possession 2028</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
